import type { WebediaPlayer } from '@webedia-services/webedia-player';
import {
  DailymotionPlayer,
  DailymotionPlayerState,
  WebediaDailymotionPlayer
} from '@webedia-services/webedia-player/dist/types/player/dailymotion/types';
import type { TimeEvent } from '@webedia-services/webedia-player/dist/types/webediaEvents';

import { ROUTE } from 'common/configuration/constants';
import { DM_PREMIUM_KEYWORD } from 'common/constants/Ads';
import { getUrlParam } from 'common/tools/network/url';
import slugify from 'common/tools/string/slugify';
import { hit } from 'common/tracking/jan';

import type { PlayerCustomData, PlayerVideo } from 'website/module/player';
import { ExtraProps } from 'website/tracking/player';
import {
  CATEGORY_VIDEO_PUB_ALLOCINE_NATIVE,
  getCurrentCustomData,
  getVideoDisplayHostname,
  getVideoPubFormatValue,
  mainContentCategorySelector,
  retreiveEntityId
} from 'website/tracking/player/utils';

// Constants for event details
const EVENT_POSITION_THRESHOLD = 3;
const PARTNER_NAME = 'ac_footer';

// Here is a flag to indicate if the video source (ads, video content) is first play or not on the player
let callVastCalled = false;
let customStartEventSent = false;
let playerStartEventSent = false;

const route =
  ROUTE && ROUTE.length ? ROUTE : getUrlParam('displaycontext') ?? null;
const siteRoute = route ? { site_route: route } : {};

const formatData = (
  data: Record<string, string | number | null | boolean | undefined>
) =>
  Object.entries(data).reduce<Record<string, string | number | boolean>>(
    (acc, [key, value]) => ({
      ...acc,
      [key]: value || value === false ? value : 'undefined'
    }),
    {}
  );

const getQuartile = (quartile: number) => {
  let progression = 'starts';
  if (quartile > 0) {
    progression = `play_${quartile * 25}`;
  }

  if (quartile >= 4) {
    progression = 'finish';
  }
  return progression;
};

const matchCurrentVideo = (
  customData: PlayerCustomData,
  extra: ExtraProps,
  state?: DailymotionPlayerState
):
  | Pick<PlayerVideo, 'id' | 'relatedEntityId' | 'relatedEntityTitle'>
  | undefined => {
  let videoData;
  if (state) {
    videoData = customData.videos?.find(
      video => video.idDailymotion === state.videoId
    );
  }
  if (!videoData) {
    const jadTrailer = extra.postrollConfig?.postroll?.find(
      video => video.dailymotionId === state?.videoId
    );
    if (jadTrailer) {
      videoData = {
        id: retreiveEntityId(jadTrailer.xt_title),
        relatedEntityId: jadTrailer.ref,
        relatedEntityTitle: jadTrailer.refTitle
      };
    }
  }
  return videoData;
};

const buildGlobalTracking = (noAdsServer?: boolean) =>
  formatData({
    global_adserved: !noAdsServer,
    global_branded: !!noAdsServer
  });

const getVideoPlayerType = (customData: PlayerCustomData) => {
  const videoPubFormat = getVideoPubFormatValue(customData);

  return videoPubFormat.length
    ? `native_ads_${videoPubFormat}`
    : 'adserver_player';
};

const buildPlayerTracking = (customData: PlayerCustomData) =>
  formatData({
    video_player_type: getVideoPlayerType(customData)
  });

const buildVideoTracking = (
  customData: PlayerCustomData,
  currentVideo?: PlayerVideo,
  state?: DailymotionPlayerState,
  config?: ReturnType<DailymotionPlayer['getSettings']>,
  extra?: ExtraProps
) =>
  formatData({
    dm_autostart: config?.autostart,
    dm_pip: state?.playerPipStatus === 'enabled' ? 'on' : 'off',
    dm_player_id: customData.playerDmId,
    dm_video_id: state?.videoId,
    vast_url: extra?.customParams,
    video_display_hostname: getVideoDisplayHostname(customData),
    video_playing_id: currentVideo?.id,
    video_related_entity: slugify(currentVideo?.relatedEntityTitle),
    video_related_entity_id: currentVideo?.relatedEntityId,
    video_title: slugify(state?.videoTitle)
  });

const sendHit = async (
  originalPlayer: WebediaDailymotionPlayer,
  customData: PlayerCustomData,
  extra: ExtraProps,
  addGlobalTracking: boolean,
  addPlayerTracking: boolean,
  eventName: string,
  data?: Record<string, string | undefined>
) => {
  const state = await originalPlayer?.getState();
  const config = originalPlayer?.getSettings();
  const currentVideo = matchCurrentVideo(customData, extra, state);

  const videoTracking = buildVideoTracking(
    customData,
    currentVideo,
    state,
    config,
    extra
  );

  let globalTracking = {};
  let playerTracking = {};

  if (addGlobalTracking) {
    globalTracking = buildGlobalTracking(
      !extra?.customParams || extra.customParams === DM_PREMIUM_KEYWORD
    );
  }

  if (addPlayerTracking) {
    playerTracking = buildPlayerTracking(customData);
  }

  hit(eventName, {
    ...videoTracking,
    ...globalTracking,
    ...playerTracking,
    ...data,
    ...siteRoute
  });
};

const initJan = (
  webediaPlayer: WebediaPlayer,
  customData: PlayerCustomData,
  extra: ExtraProps
) => {
  const send = sendHit.bind(
    null,
    webediaPlayer.originalPlayer as WebediaDailymotionPlayer,
    customData,
    extra
  );

  const sendVideoContentEvent = (event: string, filters_value?: string) => {
    const extraData = filters_value ? { filters_value } : {};
    const eventName =
      mainContentCategorySelector(webediaPlayer, customData) ===
      CATEGORY_VIDEO_PUB_ALLOCINE_NATIVE
        ? 'video_pub_allocine_native'
        : 'video_content';

    send(true, true, `${eventName}_${event}`, {
      event_category: eventName,
      event_subcategory: event,
      ...extraData
    });
  };

  const sendRefresh = (event: TimeEvent) => {
    if (!customStartEventSent) {
      if (event.detail.position >= EVENT_POSITION_THRESHOLD) {
        const currentCustomData = getCurrentCustomData(
          customData,
          webediaPlayer
        );
        if (currentCustomData.partner?.name === PARTNER_NAME) {
          customStartEventSent = true;

          // Send the start event
          send(true, true, `video_pub_allocine_native_refresh`, {
            event_category: 'video_pub_allocine_native',
            event_subcategory: 'refresh'
          });

          webediaPlayer.removeEventListener('time', sendRefresh);
        }
      }
    }
  };

  const sendPlayerStart = () => {
    if (!playerStartEventSent) {
      playerStartEventSent = true;
      send(true, true, 'video_player_starts', {
        event_category: 'video_player',
        event_subcategory: 'starts'
      });
      webediaPlayer.removeEventListener('player-start', sendPlayerStart);
    }
  };

  /** --- PLAYER --- */

  webediaPlayer.addEventListener('player-start', sendPlayerStart);

  /** --- PREROLL --- */

  webediaPlayer.addEventListener('ad-request', () => {
    if (!callVastCalled) {
      callVastCalled = true;
      send(true, true, 'video_player_call_vast', {
        event_category: 'video_player',
        event_subcategory: 'call_vast'
      });
    }
  });

  webediaPlayer.addEventListener('quartile-advertisement', event => {
    const progression = getQuartile(event.detail.quartile);

    send(false, false, `video_preroll_${progression}`, {
      event_category: 'video_preroll',
      event_subcategory: progression
    });
  });

  webediaPlayer.addEventListener('ad-skipped', () => {
    send(false, false, 'video_preroll_skip', {
      event_category: 'video_preroll',
      event_subcategory: 'skip'
    });
  });

  webediaPlayer.addEventListener('ad-error', event => {
    send(false, false, 'video_preroll_crash_details', {
      event_category: 'video_preroll',
      event_subcategory: 'crash_details',
      filters_value: event.detail.message
    });
  });

  webediaPlayer.addEventListener('ad-click', () => {
    send(false, false, 'video_preroll_click', {
      event_category: 'video_preroll',
      event_subcategory: 'click'
    });
  });

  /** --- VIDEO --- */

  webediaPlayer.addEventListener('quartile', event => {
    const progression = getQuartile(event.detail.quartile);

    sendVideoContentEvent(progression);
  });

  webediaPlayer.addEventListener('error', event => {
    if (webediaPlayer.postroll?.isPlayingPostrollVideo()) {
      send(false, false, 'video_postroll_crash_details', {
        event_category: 'video_postroll',
        event_subcategory: 'crash_details',
        filters_value: event.detail.message
      });
    } else {
      sendVideoContentEvent('crash_details', event.detail.message);
    }
  });

  webediaPlayer.addEventListener('time', sendRefresh);

  /** --- OVERLAY --- */

  webediaPlayer.addEventListener('next-video-shown', () =>
    send(false, false, 'video_postroll_preview_overlay_appears', {
      event_category: 'video',
      event_subcategory: 'preview_overlay_appears'
    })
  );

  webediaPlayer.addEventListener('next-video-closed', () =>
    send(false, false, 'video_postroll_preview_overlay_close', {
      event_category: 'video',
      event_subcategory: 'preview_overlay_close'
    })
  );

  webediaPlayer.addEventListener('currently-playing-shown', () =>
    send(false, false, 'video_postroll_overlay_appears', {
      event_category: 'video',
      event_subcategory: 'overlay_appears'
    })
  );

  webediaPlayer.addEventListener('currently-playing-banner-clicked', () =>
    send(false, false, 'video_postroll_overlay_click', {
      event_category: 'video',
      event_subcategory: 'overlay_click'
    })
  );

  /** --- POSTROLL --- */

  webediaPlayer.addEventListener('postroll-start', () =>
    send(true, false, 'video_postroll', {
      event_category: 'video',
      event_subcategory: 'video_postroll'
    })
  );

  webediaPlayer.addEventListener('quartile-postroll', event => {
    const progression = getQuartile(event.detail.quartile);

    send(true, false, `video_postroll_${progression}`, {
      event_category: 'video_postroll',
      event_subcategory: progression
    });
  });

  webediaPlayer.addEventListener('playlist-complete', () => {
    if (!webediaPlayer.postroll?.isPlayingPostrollVideo()) {
      send(false, false, 'video_postroll_empty', {
        event_category: 'video_postroll',
        event_subcategory: 'empty'
      });
    }
  });
};

export default initJan;
